import { action, flow, makeObservable, observable } from 'mobx';
import { LoanStore } from './loanStore';
import { FormStore, GlobalStore } from '../../../../feature-app-core/src/stores';
import { ManualQuoteService } from '../services/manualQuoteService';
import { REQUIRED_ERROR_MESSAGE } from '@roc/feature-valuation-review-v2';
import { isNil, isStabilizedBridge } from '@roc/feature-utils';

const form = {
  fields: {
    acceptCheck: {
      value: false,
      error: null,
      rule: '',
      message: REQUIRED_ERROR_MESSAGE,
    },
  },
};

const declineForm = {
  fields: {
    message: {
      value: '',
      error: null,
      rule: '',
      message: REQUIRED_ERROR_MESSAGE,
    },
  },
};

export class ManualQuoteStore {
  globalStore: GlobalStore;
  loanStore: LoanStore;
  manualQuoteFormStore: FormStore;
  declineQuoteFormStore: FormStore;
  manualQuoteService: ManualQuoteService;

  portal: 'nportal' | 'cportal';

  constructor(globalStore, loanStore) {
    this.loanStore = loanStore;
    this.manualQuoteFormStore = new FormStore(form, globalStore);
    this.declineQuoteFormStore = new FormStore(declineForm, globalStore);
    this.manualQuoteService = new ManualQuoteService();

    makeObservable(this, {
      portal: observable,
      saveReviseTerms: flow,
      declineManualQuote: flow,
      acceptManualQuote: flow,
      validateDeclineReason: action,
    });
  }

  validateDeclineReason(): boolean {
    const isValid = !!this.declineQuoteFormStore.getValue('message')?.trim();
    this.declineQuoteFormStore.form.fields.message.error = isValid ? null : REQUIRED_ERROR_MESSAGE;
    return isValid;
  }

  *saveReviseTerms(quoteData, user) {
    const declineValues = this.declineQuoteFormStore.getFormValues();
    const reviseTermData = {
      loanId: this.loanStore?.loanDetails?.loanId,
      source: this.portal,
      action: this.portal === 'nportal' ? "Updated" : "Requested",
      comments: declineValues.message,
      actorRole: user?.roles[0],
      actorName: user?.fullName,
      fees: quoteData?.loanEconomics.fees,
      loanTermMonths: quoteData?.loanEconomics.loanTermMonths,
      interestRate: quoteData?.loanEconomics.interestRate,
      loanToCost: quoteData?.loanEconomics.manualQuoteLoantoCost,
      constructionReserve: quoteData?.loanEconomics.manualQuoteConstructionReserve,
      arltv: quoteData?.loanEconomics.exceptionTotalARLTVPercentage,
      totalLoanToCost: isStabilizedBridge(quoteData?.loanSubtype) ? quoteData?.loanEconomics.manualQuoteLoantoCost : quoteData?.loanEconomics.exceptionTotalLoanCostPercentage,
    }
    yield this.loanStore.submitLoanChangeRequest(reviseTermData);
    this.loanStore.fetchLoanChangeRequests(this.loanStore?.loanDetails?.loanId);
  }

  *declineManualQuote(data) {
    if (!this.validateDeclineReason()) {
      return;
    }
    const declineValues = this.declineQuoteFormStore.getFormValues();
    const reviseTermData = {
      ...data,
      comments: declineValues.message,
    }
    yield this.manualQuoteService.declineManualQuote(reviseTermData);
    this.declineQuoteFormStore.reset();
    if (!isNil(this.loanStore?.loanDetails?.loanId)) {
      this.loanStore.fetchLoanChangeRequests(this.loanStore?.loanDetails?.loanId);
    }
  }

  *acceptManualQuote(quoteData, user, onSuccess: () => void) {
    const reviseTermData = {
      loanId: this.loanStore?.loanDetails?.loanId,
      source: this.portal,
      action: "Accepted",
      actorRole: user?.roles[0],
      actorName: user?.fullName,
      fees: quoteData?.loanEconomics.fees,
      loanTermMonths: quoteData?.loanEconomics.loanTermMonths,
      interestRate: quoteData?.loanEconomics.interestRate,
      loanToCost: quoteData?.loanEconomics.manualQuoteLoantoCost,
      constructionReserve: quoteData?.loanEconomics.manualQuoteConstructionReserve,
      arltv: quoteData?.loanEconomics.exceptionTotalARLTVPercentage,
      totalLoanToCost: isStabilizedBridge(quoteData?.loanSubtype) ? quoteData?.loanEconomics?.manualQuoteLoantoCost : quoteData?.loanEconomics?.exceptionTotalLoanCostPercentage,
    }
    yield this.loanStore.submitLoanChangeRequest(reviseTermData);
    this.loanStore.fetchLoanChangeRequests(this.loanStore?.loanDetails?.loanId);
    onSuccess();
  }
}

export default ManualQuoteStore;
